import React, { Children } from 'react';
import { motion } from 'framer-motion';
type Service = {
  title: string;
  description: string;
  price: string;
  duration: string;
  icon: React.ReactNode;
};
function BotanicalIcon({
  variant
}: {
  variant: 'aromatherapy' | 'lymphatic' | 'trigger' | 'somatic';
}) {
  const icons = {
    aromatherapy: <svg viewBox="0 0 80 80" fill="none" className="w-full h-full" data-id="element-223">
        <circle cx="40" cy="50" r="20" fill="currentColor" fillOpacity="0.1" data-id="element-224" />
        <path d="M40 15C40 15 30 25 30 35C30 45 40 50 40 50C40 50 50 45 50 35C50 25 40 15 40 15Z" fill="currentColor" fillOpacity="0.15" data-id="element-225" />
        <path d="M40 20V48" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" data-id="element-226" />
        <ellipse cx="40" cy="55" rx="12" ry="6" fill="currentColor" fillOpacity="0.08" data-id="element-227" />
        <path d="M35 30C30 32 28 36 28 38" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.75" data-id="element-228" />
        <path d="M45 32C50 34 52 37 52 40" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.75" data-id="element-229" />
        {/* Essential oil drops */}
        <circle cx="32" cy="62" r="2" fill="currentColor" fillOpacity="0.2" data-id="element-230" />
        <circle cx="48" cy="60" r="1.5" fill="currentColor" fillOpacity="0.15" data-id="element-231" />
        <circle cx="40" cy="65" r="1.5" fill="currentColor" fillOpacity="0.18" data-id="element-232" />
      </svg>,
    lymphatic: <svg viewBox="0 0 80 80" fill="none" className="w-full h-full" data-id="element-233">
        {/* Flowing water/lymph representation */}
        <path d="M20 40C25 35 35 45 40 40C45 35 55 45 60 40" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" fill="none" data-id="element-234" />
        <path d="M20 50C25 45 35 55 40 50C45 45 55 55 60 50" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1.5" fill="none" data-id="element-235" />
        <path d="M20 60C25 55 35 65 40 60C45 55 55 65 60 60" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" fill="none" data-id="element-236" />
        {/* Leaf accent */}
        <path d="M40 15C40 15 32 22 32 30C32 38 40 42 40 42C40 42 48 38 48 30C48 22 40 15 40 15Z" fill="currentColor" fillOpacity="0.12" data-id="element-237" />
        <path d="M40 18V40" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.75" data-id="element-238" />
      </svg>,
    trigger: <svg viewBox="0 0 80 80" fill="none" className="w-full h-full" data-id="element-239">
        {/* Concentric circles representing pressure points */}
        <circle cx="40" cy="40" r="25" stroke="currentColor" strokeOpacity="0.1" strokeWidth="1" fill="none" data-id="element-240" />
        <circle cx="40" cy="40" r="18" stroke="currentColor" strokeOpacity="0.15" strokeWidth="1" fill="none" data-id="element-241" />
        <circle cx="40" cy="40" r="11" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1" fill="none" data-id="element-242" />
        <circle cx="40" cy="40" r="5" fill="currentColor" fillOpacity="0.25" data-id="element-243" />
        {/* Small botanical accents */}
        <path d="M15 25C15 25 12 30 12 35C12 40 15 42 15 42" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-244" />
        <path d="M65 55C65 55 68 50 68 45C68 40 65 38 65 38" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-245" />
      </svg>,
    somatic: <svg viewBox="0 0 80 80" fill="none" className="w-full h-full" data-id="element-246">
        {/* Body-mind connection - organic flowing shape */}
        <path d="M40 10C40 10 25 20 25 40C25 60 40 70 40 70C40 70 55 60 55 40C55 20 40 10 40 10Z" fill="currentColor" fillOpacity="0.1" data-id="element-247" />
        <path d="M40 15V65" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" data-id="element-248" />
        {/* Branching paths representing nervous system */}
        <path d="M40 30C35 32 30 35 28 40" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-249" />
        <path d="M40 30C45 32 50 35 52 40" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-250" />
        <path d="M40 45C35 47 32 50 30 55" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-251" />
        <path d="M40 45C45 47 48 50 50 55" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.75" data-id="element-252" />
        <circle cx="40" cy="25" r="3" fill="currentColor" fillOpacity="0.2" data-id="element-253" />
      </svg>
  };
  return icons[variant];
}
const services: Service[] = [{
  title: 'Aromatherapy Massage',
  description: 'A deeply relaxing experience combining therapeutic touch with carefully selected essential oils to restore harmony and ease tension.',
  price: '€85',
  duration: '60 min',
  icon: <BotanicalIcon variant="aromatherapy" data-id="element-254" />
}, {
  title: 'Lymphatic Drainage',
  description: "Gentle, rhythmic movements to support your body's natural detoxification process and reduce fluid retention.",
  price: '€95',
  duration: '75 min',
  icon: <BotanicalIcon variant="lymphatic" data-id="element-255" />
}, {
  title: 'Trigger Point Therapy',
  description: 'Focused pressure techniques to release chronic muscle tension and alleviate pain at its source.',
  price: '€90',
  duration: '60 min',
  icon: <BotanicalIcon variant="trigger" data-id="element-256" />
}, {
  title: 'Somatic Bodywork',
  description: 'An integrative approach connecting body and mind, releasing stored tension and promoting emotional well-being.',
  price: '€110',
  duration: '90 min',
  icon: <BotanicalIcon variant="somatic" data-id="element-257" />
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export function Services() {
  return <section id="services" className="bg-sage py-24 md:py-32" data-id="element-258">
      <div className="max-w-7xl mx-auto px-6" data-id="element-259">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-260">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium text-cream mb-4" data-id="element-261">
            Our Services
          </h2>
          <p className="font-sans text-cream/80 max-w-2xl mx-auto text-lg" data-id="element-262">
            Each treatment is thoughtfully designed to address your unique
            needs, drawing from time-honored techniques and the healing power of
            nature.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-263">
          {services.map(service => <motion.article key={service.title} variants={cardVariants} className="bg-cream rounded-3xl p-8 md:p-10 shadow-botanical-lg group" data-id="element-264">
              <div className="flex flex-col h-full" data-id="element-265">
                <div className="w-20 h-20 mb-6 text-sage" data-id="element-266">{service.icon}</div>

                <h3 className="font-serif text-2xl md:text-3xl font-medium text-botanical-text mb-3" data-id="element-267">
                  {service.title}
                </h3>

                <p className="font-sans text-botanical-muted leading-relaxed mb-6 flex-grow" data-id="element-268">
                  {service.description}
                </p>

                <div className="flex items-center justify-between pt-6 border-t border-eucalyptus/30" data-id="element-269">
                  <div className="flex items-baseline gap-2" data-id="element-270">
                    <span className="font-serif text-2xl font-medium text-olive" data-id="element-271">
                      {service.price}
                    </span>
                    <span className="font-sans text-sm text-botanical-muted" data-id="element-272">
                      / {service.duration}
                    </span>
                  </div>

                  <a href="#location" className="inline-flex items-center gap-2 font-sans text-sm font-medium text-olive hover:text-olive-dark transition-colors duration-300" aria-label={`Book ${service.title}`} data-id="element-273">
                    Book Now
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-274">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-275" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.article>)}
        </motion.div>
      </div>
    </section>;
}